import {
  type AttachmentTarget,
  type AttachmentTypeCode,
  type ComplianceSubject,
  extractApiErrorMessage,
  type TransportAssoc,
  useCreateMessageMutation,
  useGetTicketBulkPdfLinkMutation,
  useGetTicketExcelLinkMutation,
  useGetTicketPdfLinkMutation,
} from "@ots-frontend/ots-data";
import { useContext, useState } from "react";
import { AppContext, type AppContextType } from "../../app/AppContext";
import { ConnectedAttachmentManagerDialog } from "../../attachments/connected/ConnectedAttachmentManager";
import { ComplianceWorkspace } from "../../compliance/ComplianceWorkspace";
import { useComplianceAvailability } from "../../compliance/connected/useComplianceAvailability";
import { ConnectedGaugeQuantityAuditDialog } from "../../gauge-quantity/connected/ConnectedGaugeQuantityAuditDialog";
import { ConnectedTicketCostDialog } from "../../ticket-cost/connected/ConnectedTicketCostDialog";
import { CommentDialog } from "../../dialogs/CommentDialog";
import { useOptionalOtsToast } from "../../components/OtsToast";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import type { ActivityEntry } from "../driver-activity-view-model";
import { createActionProvider, type DriverActivityAction, type DriverActivityActionProvider } from "../driver-activity-actions";

type Side = "pickup" | "delivery";

const transportOf = (entry: ActivityEntry | undefined, side: Side) => side === "pickup" ? entry?.pickup : entry?.delivery;
const tenantOf = (entry: ActivityEntry, side: Side) => entry.tta?.database ?? transportOf(entry, side)?.dbName;
const ticketsOf = (entries: ActivityEntry[]): TransportAssoc[] => entries.flatMap((entry) => entry.tta ? [entry.tta] : []);

/** What compliance is checked for: the ticket, or a lone pickup (WaterGo's `ComplianceSubject`). */
export const complianceSubjectOf = (entry: ActivityEntry | undefined): ComplianceSubject | undefined =>
  entry?.tta?.id ? { kind: "ticket", id: entry.tta.id, tenantCode: entry.tta.database }
    : entry?.pickup?.id ? { kind: "pickup", id: entry.pickup.id, tenantCode: entry.pickup.dbName }
      : undefined;

/** WaterGo's transport attachment categories. */
const TRANSPORT_ATTACHMENT_TYPES: AttachmentTypeCode[] = ["TICKET", "REGULATORY_FORM", "SIGNATURE", "FILE"];

/**
 * WaterGo's Driver Activity tools that act on a ticket's pickup, delivery, or the ticket itself: comments,
 * attachments, compliance, cost, gauge quantity audits, and the Ticket PDF / Ticket Excel downloads. Tickets
 * raised inside site work never reach the workspace, so every row these see is a load, a pickup, or a delivery.
 * The hook narrows Compliance further to tickets a compliance program applies to.
 */
export const DRIVER_ACTIVITY_TICKET_TOOL_ACTIONS: DriverActivityAction[] = [
  { id: "comment-pickup", label: "Add Pickup Comment", scope: "single", group: "tools", isEnabled: ([entry]) => !!entry?.pickup?.id },
  { id: "comment-delivery", label: "Add Delivery Comment", scope: "single", group: "tools", isEnabled: ([entry]) => !!entry?.delivery?.id },
  { id: "attachments", label: "Attachments", scope: "single", group: "tools", readOnly: true, isEnabled: ([entry]) => !!(entry?.pickup?.id || entry?.delivery?.id) },
  { id: "compliance", label: "Compliance", scope: "single", group: "tools", readOnly: true, isEnabled: ([entry]) => !!complianceSubjectOf(entry) },
  { id: "download-pdf", label: "Download Ticket PDF", scope: "any", group: "tools", readOnly: true, isEnabled: (entries) => ticketsOf(entries).length > 0 },
  { id: "download-excel", label: "Download Ticket Excel", scope: "any", group: "tools", readOnly: true, isEnabled: (entries) => ticketsOf(entries).length > 0 },
  { id: "cost", label: "Ticket Cost", scope: "single", group: "tools", readOnly: true, isEnabled: ([entry]) => !!entry?.tta?.id },
  { id: "gauge-audit-pickup", label: "Pickup Gauge Quantity Audit", scope: "single", group: "tools", readOnly: true, isEnabled: ([entry]) => !!entry?.pickup?.id },
  { id: "gauge-audit-delivery", label: "Delivery Gauge Quantity Audit", scope: "single", group: "tools", readOnly: true, isEnabled: ([entry]) => !!entry?.delivery?.id },
];

export type UseDriverActivityTicketToolsOptions = {
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  /** A download that could not be prepared; a toast from the nearest OtsToastProvider by default. */
  onError?: (message: string) => void;
  /** Opens a download link; a new tab by default. */
  openLink?: (url: string) => void;
  /** Whether this person holds a permission; everything is offered when omitted. */
  can?: (permission: string) => boolean;
};

/** What a tool needs beyond seeing the page: a ticket's cost is for those who may see costs. */
const TICKET_TOOL_PERMISSIONS: Partial<Record<string, string>> = { cost: "view_cost" };

/** The ticket tools as an action provider: combine it with the host's own actions. */
export type DriverActivityTicketTools = DriverActivityActionProvider;

/** Comments, attachments, compliance, cost, gauge audits, and downloads for a Driver Activity host, wired to their services. */
export const useDriverActivityTicketTools = ({
  brand,
  colorScheme,
  onError: onErrorOption,
  openLink = (url) => window.open(url, "_blank", "noopener"),
  can = () => true,
}: UseDriverActivityTicketToolsOptions = {}): DriverActivityTicketTools => {
  const toast = useOptionalOtsToast();
  const onError = onErrorOption ?? (toast && ((message: string) => void toast.show({title: "Download failed", description: message, tone: "danger"})));
  // The cost and gauge dialogs default to WaterGo's brand; in another app they take that app's.
  const app = useContext(AppContext) as AppContextType | undefined;
  const dialogBrand = brand ?? app?.brand;
  const compliance = useComplianceAvailability();
  const [costFor, setCostFor] = useState<ActivityEntry>();
  const [gaugeFor, setGaugeFor] = useState<{ entry: ActivityEntry; side: Side }>();
  const [comment, setComment] = useState<{ entry: ActivityEntry; side: Side }>();
  const [commentError, setCommentError] = useState<string>();
  const [attachmentsFor, setAttachmentsFor] = useState<ActivityEntry>();
  const [complianceFor, setComplianceFor] = useState<ActivityEntry>();
  const [createMessage, creating] = useCreateMessageMutation();
  const [getPdfLink] = useGetTicketPdfLinkMutation();
  const [getBulkPdfLink] = useGetTicketBulkPdfLinkMutation();
  const [getExcelLink] = useGetTicketExcelLinkMutation();

  const download = (request: Promise<string>, what: string) => request
    .then((url) => url ? openLink(url.trim()) : onError?.(`The ${what} came back empty.`))
    .catch((failure) => onError?.(extractApiErrorMessage(failure) ?? `The ${what} could not be prepared.`));

  const openComment = (side: Side) => ([entry]: ActivityEntry[]) => {
    if (!entry) return;
    setCommentError(undefined);
    setComment({ entry, side });
  };
  const handlers: Record<string, (entries: ActivityEntry[]) => void> = {
    "comment-pickup": openComment("pickup"),
    "comment-delivery": openComment("delivery"),
    attachments: ([entry]) => setAttachmentsFor(entry),
    compliance: ([entry]) => setComplianceFor(entry),
    cost: ([entry]) => setCostFor(entry),
    "gauge-audit-pickup": ([entry]) => entry && setGaugeFor({ entry, side: "pickup" }),
    "gauge-audit-delivery": ([entry]) => entry && setGaugeFor({ entry, side: "delivery" }),
    "download-pdf": (entries) => {
      const tickets = ticketsOf(entries);
      if (tickets.length === 1) download(getPdfLink({ id: tickets[0].id, database: tickets[0].database }).unwrap(), "ticket PDF");
      else if (tickets.length > 1) download(getBulkPdfLink(tickets).unwrap(), "ticket PDFs");
    },
    "download-excel": (entries) => {
      const tickets = ticketsOf(entries);
      if (tickets.length) download(getExcelLink({ ttas: tickets }).unwrap(), "ticket spreadsheet");
    },
  };

  const commentTransport = comment && transportOf(comment.entry, comment.side);
  const attachmentTargets: AttachmentTarget[] = attachmentsFor
    ? (["pickup", "delivery"] as const).flatMap((side) => {
      const transport = transportOf(attachmentsFor, side);
      return transport?.id
        ? [{ objectId: transport.id, objectType: "TRANSPORT", label: side === "pickup" ? "Pickup" : "Delivery", tenantCode: tenantOf(attachmentsFor, side) }]
        : [];
    })
    : [];
  const complianceSubject = complianceSubjectOf(complianceFor);
  const gaugeTransport = gaugeFor && transportOf(gaugeFor.entry, gaugeFor.side);

  const dialogs = <>
    <CommentDialog
      open={!!commentTransport}
      title={comment?.side === "delivery" ? "New Delivery Comment" : "New Pickup Comment"}
      description={comment && [comment.entry.resourceId, commentTransport?.ticketNumber && `#${commentTransport.ticketNumber}`].filter(Boolean).join(" · ")}
      required
      requiredMessage="Enter a comment."
      isSubmitting={creating.isLoading}
      error={commentError}
      onCancel={() => setComment(undefined)}
      onSubmit={(text, details) => {
        if (!text || !comment || !commentTransport?.id) return;
        setCommentError(undefined);
        createMessage({
          objectId: commentTransport.id,
          objectType: "TRANSPORT",
          messageType: details.messageType,
          message: text,
          tenantCode: tenantOf(comment.entry, comment.side),
        }).unwrap()
          .then(() => setComment(undefined))
          .catch((failure) => setCommentError(extractApiErrorMessage(failure) ?? "The comment could not be saved."));
      }}/>
    <ConnectedAttachmentManagerDialog
      open={attachmentTargets.length > 0}
      onClose={() => setAttachmentsFor(undefined)}
      targets={attachmentTargets}
      attachmentTypes={TRANSPORT_ATTACHMENT_TYPES}
      title={attachmentsFor ? `Attachments · ${attachmentsFor.resourceId ?? "Ticket"}` : undefined}
      brand={brand}
      colorScheme={colorScheme}/>
    {complianceSubject && (
      <ComplianceWorkspace
        presentation="dialog"
        subject={complianceSubject}
        subjectLabel={complianceSubject.kind === "ticket" ? "Ticket compliance" : "Pickup compliance"}
        brand={dialogBrand}
        compact
        onClose={() => setComplianceFor(undefined)}/>
    )}
    {costFor?.tta && (
      <ConnectedTicketCostDialog
        open
        onClose={() => setCostFor(undefined)}
        ticketId={costFor.tta.id}
        database={costFor.tta.database}
        title={costFor.resourceId ?? "Cost details"}
        brand={dialogBrand}
        colorScheme={colorScheme}/>
    )}
    {gaugeTransport?.id && gaugeFor && (
      <ConnectedGaugeQuantityAuditDialog
        open
        onClose={() => setGaugeFor(undefined)}
        transportId={gaugeTransport.id}
        database={tenantOf(gaugeFor.entry, gaugeFor.side)}
        title={["Gauge quantity", gaugeFor.side === "pickup" ? "Pickup" : "Delivery", gaugeFor.entry.resourceId].filter(Boolean).join(" · ")}
        brand={dialogBrand}
        colorScheme={colorScheme}/>
    )}
    {compliance.probes}
  </>;

  // Compliance is offered, as in WaterGo, only once a lookup finds a program for the ticket.
  const offered = DRIVER_ACTIVITY_TICKET_TOOL_ACTIONS.filter((action) => {
    const permission = TICKET_TOOL_PERMISSIONS[action.id];
    return !permission || can(permission);
  });
  const actions = offered.map((action) => action.id === "compliance"
    ? { ...action, isVisible: ([entry]: ActivityEntry[]) => {
      const subject = complianceSubjectOf(entry);
      return !!subject && compliance.isAvailable(subject) === true;
    } }
    : action);

  return createActionProvider(actions, handlers, dialogs);
};
